import { useCallback, useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { useMutation } from '@tanstack/react-query'

import { FeedbackContext } from '~/components/feedback'
import {
  externalCampaignMetricsKey,
  ExternalCampaignMetricsService
} from '~/modules/retailMedia/services/metrics'

type ExternalCampaignMetricsParams = {
  file: File
  channel: string
  placement_name: string
}

type UseExternalCampaignMetricsProps = {
  onSuccess?: (response: unknown) => void
}

export const useExternalCampaignMetrics = ({
  onSuccess
}: UseExternalCampaignMetricsProps = {}) => {
  const { t } = useTranslation()
  const { toast } = useContext(FeedbackContext)

  const mutationFn = useCallback(
    async ({
      file,
      channel,
      placement_name
    }: ExternalCampaignMetricsParams) => {
      const formData = new FormData()

      formData.append('file', file)
      formData.append('channel', channel)
      formData.append('placement_name', placement_name)

      return ExternalCampaignMetricsService.upload({ body: formData })
    },
    []
  )

  const handleSuccess = useCallback(
    (response: { data: unknown }) => {
      toast.success(t('rm:externalCampaignMetrics.mutation.success'))
      onSuccess?.(response.data)
    },
    [toast, t, onSuccess]
  )

  const { mutate, isPending, error, reset } = useMutation({
    mutationKey: [externalCampaignMetricsKey],
    mutationFn,
    onSuccess: handleSuccess
  })

  const handleImport = useCallback(
    (params: ExternalCampaignMetricsParams) => {
      mutate(params)
    },
    [mutate]
  )

  return { mutate: handleImport, isPending, isLoading: isPending, error, reset }
}
